import "@cyntler/react-doc-viewer/dist/index.css";
import { faEye } from '@fortawesome/pro-duotone-svg-icons';
import { faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Card, Select, Table, message } from 'antd';
import { Option } from 'antd/es/mentions';
import { useState } from 'react';
import dayjs, { fromUtc } from '../helpers/dayjsSetup';
import DocumentService from '../services/Document/DocumentService';
import DocumentPreviewModal from './DocumentPreviewModal';

const DocumentTable = ({ items, totalCount, pageNumber, pageSize, onPageChange, handleViewDetails }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    const handlePreview = (record) => {
        setSelectedDocument(record);
        setIsModalVisible(true);
    };

    const columns = [
        {
            title: '',
            key: 'view',
            fixed: 'left',
            width: 50,
            render: (_, record) => (
                <Button
                    type="link"
                    onClick={() => handlePreview(record)}
                >
                    <FontAwesomeIcon
                        icon={faEye}
                        className="text-2xl text-black block p-2 hover:text-cyan-500"
                    />
                </Button>

            ),
        }, {
            title: "File Name",
            dataIndex: "originalFileName",
            width: 300,
            render: (text, record, row, index) => {
                const handleDownload = async () => {
                    try {
                        const response = await DocumentService.handleDownload(record?.documentID);
                        const contentDisposition = response.headers["content-disposition"];
                        let fileName = response.fileName || "download";
                        if (contentDisposition) {
                            const utf8Match = contentDisposition.match(/filename\*\=UTF-8''([^;]+)/);
                            if (utf8Match) {
                                fileName = decodeURIComponent(utf8Match[1]);
                            }
                        }

                        const blob = new Blob([response.data]);
                        const blobUrl = window.URL.createObjectURL(blob);

                        const link = document.createElement("a");
                        link.href = blobUrl;
                        link.download = fileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl);
                    } catch (error) {
                        if (error?.status === 404) {
                            message.error("Document Not Found");
                        } else {
                            message.error("Something went wrong while downloading the document");
                        }
                        console.error("Document Download Failed. ", error);
                    }
                };
                return (
                    <div className="grid">
                        <a
                            onClick={handleDownload}
                            className="p-0 cursor-pointer text-black text-sm"
                        >
                            {record.OriginalFileName || record.originalFileName}
                        </a>
                    </div>
                )
            },
        }, {
            title: 'Supplier',
            dataIndex: 'supplierShortCode',
            key: 'supplierShortCode',
            width: 120,
            render: (value) => <div className="font-semibold">{value}</div>,
            sorter: (a, b) => (a.supplierName || '').localeCompare(b.supplierName || '')
        },

        {
            title: 'BL',
            dataIndex: 'blNum',
            key: 'blNum',
            width: 100,
            sorter: (a, b) => (a.blNum || '').localeCompare(b.blNum || '')
        },
        // {
        //     title: 'Download',
        //     key: 'download',
        //     width: 100,
        //     render: (_, record) => (
        //         <Tooltip title="Download file">
        //             <Button
        //                 icon={<DownloadOutlined />}
        //                 type="link"
        //                 href={record.fileURL}
        //                 target="_blank"
        //             />
        //         </Tooltip>
        //     ),
        // },
        {
            title: 'Reference',
            key: 'entityRef',
            width: 180,

            sorter: (a, b) => {
                const getVal = (row) => {
                    switch (row.entityType) {
                        case 'PurchaseOrder':
                            return row.poNumber || '';
                        case 'Container':
                            return row.containerNum || '';
                        default:
                            return '';
                    }
                };

                return getVal(a).localeCompare(getVal(b));
            },

            render: (_, record) => {
                let label = '';

                switch (record.entityType) {
                    case 'PurchaseOrder':
                        label = record.poNumber || '';
                        break;

                    case 'Container':
                        label = record.containerNum || '';
                        break;

                    default:
                        label = '';
                        break;
                }

                // optional: hide button if there's no value
                if (!label) return null;

                return (
                    <Button
                        className="w-36 max-w-36 overflow-hidden whitespace-nowrap font-bold rounded-lg"
                        type="primary"
                        onClick={() => handleViewDetails(record)}
                    >
                        {label}
                    </Button>
                );
            }
        },
        {
            title: 'User Reference',
            dataIndex: 'userReference',
            key: 'userReference',
            width: 150,
            sorter: (a, b) => (a.userReference || '').localeCompare(b.userReference || '')
        },
        {
            title: 'Document Type',
            dataIndex: 'documentType',
            key: 'documentType',
            width: 200,
            sorter: (a, b) => (a.documentType || '').localeCompare(b.documentType || '')
        },
        {
            title: <div className='text-center'>Stock <div>Received</div></div>,
            dataIndex: 'isStockReceived',
            key: 'isStockReceived',
            width: 80,
            render: (val) => (
                <div className="flex items-center justify-center w-[80px]">
                    <span
                        className={`w-4 h-4 opacity-60 rounded-full ${val ? "bg-green-500" : "bg-red-500"
                            }`}
                    />
                </div>
            ),
            sorter: (a, b) => (a.isStockReceived ?? 0) - (b.isStockReceived ?? 0)
        },
        {
            title: 'Paid',
            dataIndex: 'isPaid',
            key: 'isPaid',
            width: 80,
            render: (val) => (
                <div className="flex items-center justify-center w-[80px]">
                    <span
                        className={`w-4 h-4 opacity-60 rounded-full ${val ? "bg-green-500" : "bg-red-500"
                            }`}
                    />
                </div>
            ), sorter: (a, b) => (a.isPaid ?? 0) - (b.isPaid ?? 0)
        },
        // {
        //     title: 'Uploaded Date',
        //     dataIndex: 'uploadedDate',
        //     key: 'uploadedDate',
        //     render: (value) => {
        //         if (!value) return '';
        //         const raw = dayjs(value).format('DD/MM/YYYY hh:mm A');
        //         const local = dayjs.utc(value).tz('Australia/Sydney').format('hh:mm A z');
        //         return (
        //             <Tooltip title={`Local time: ${local}`}>
        //                 <span>{raw}</span>
        //             </Tooltip>
        //         );
        //     },
        //     sorter: (a, b) => {
        //         const aDate = a.uploadedDate ? dayjs(a.uploadedDate).valueOf() : 0;
        //         const bDate = b.uploadedDate ? dayjs(b.uploadedDate).valueOf() : 0;
        //         return aDate - bDate;
        //     },
        // },
        {
            title: 'Uploaded By',
            dataIndex: 'uploadedBy',
            key: 'uploadedBy',
            render: (_, record) => {
                if (!record) return '';
                return (
                    <div>
                        <span className='font-medium'>{record.uploadedBy}</span>
                        <div>
                            <span className='text-sm'>{fromUtc(record.uploadedDate).format('DD MMM YYYY hh:mm A')}</span>
                        </div>
                    </div>
                );
            },
            sorter: (a, b) => {
                const aDate = a.uploadedDate ? dayjs(a.uploadedDate).valueOf() : 0;
                const bDate = b.uploadedDate ? dayjs(b.uploadedDate).valueOf() : 0;
                return aDate - bDate;
            },
        },
    ];

    return (
        <Card className="pb-1 rounded-3xl shadow-xl bg-white bg-opacity-80">
            <Table
                className='rounded-3xl pt-2'
                rowKey={(record) => record.documentID || `${record.containerNum}-${record.shipmentID}`}
                columns={columns}
                dataSource={Array.isArray(items) ? items : []}
                rowClassName="text-[14px]"
                // pagination={{
                //     current: pageNumber,
                //     pageSize: pageSize,
                //     total: totalCount,
                //     showSizeChanger: true,
                //     pageSizeOptions: ['10', '20', '50', '100'],
                //     onChange: (page, size) => {
                //         console.log('Pagination changed:', { page, size });
                //         onPageChange(page, size);
                //     },
                //     showTotal: (total, range) => {
                //         const noun = total === 1 ? 'document' : 'documents';
                //         return `Showing ${range[0]}-${range[1]} of ${total} ${noun}`;
                //     }
                // }}
                pagination={false}
                scroll={{ x: "max-content", y: "calc(100vh - 460px)" }}
                size='middle'
            /><div className="flex justify-between items-center mt-2">
                {/* Rows per page selector + info */}
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Rows per page:</span>
                    <Select
                        value={String(pageSize || 50)}
                        onChange={(val) => onPageChange(pageNumber, Number(val))}
                        size="medium"
                        style={{ width: 80 }}
                    >
                        <Option value="10">10</Option>
                        <Option value="20">20</Option>
                        <Option value="50">50</Option>
                        <Option value="100">100</Option>
                    </Select>
                </div>

                {/* Custom buttons */}
                <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">
                        {`${(pageNumber - 1) * pageSize + 1}-${Math.min(
                            pageNumber * pageSize,
                            totalCount
                        )} of ${totalCount} items`}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="medium"
                            onClick={() => onPageChange(1, pageSize)}
                            disabled={pageNumber === 1}
                        >
                            <FontAwesomeIcon icon={faAngleDoubleLeft} />
                        </Button>
                        <Button
                            size="medium"
                            onClick={() => onPageChange(pageNumber - 1, pageSize)}
                            disabled={pageNumber === 1}
                        >
                            <FontAwesomeIcon icon={faAngleLeft} />
                        </Button>
                        <Button
                            size="medium"
                            onClick={() => onPageChange(pageNumber + 1, pageSize)}
                            disabled={pageNumber === totalCount}
                        >
                            <FontAwesomeIcon icon={faAngleRight} />
                        </Button>
                        <Button
                            size="medium"
                            onClick={() => onPageChange(totalCount, pageSize)}
                            disabled={pageNumber === totalCount}
                        >
                            <FontAwesomeIcon icon={faAngleDoubleRight} />
                        </Button>
                    </div>
                </div>
            </div>
            <DocumentPreviewModal isOpen={isModalVisible} onClose={() => setIsModalVisible(false)} document={selectedDocument} />
        </Card >
    );
};

export default DocumentTable;



